/**
 * Customer Portal (UI) - route composition and top-level state wiring.
 */
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useState } from 'react';
import { BillingRoute } from './routes/billing';
import { PortalLayout } from './components/layout/PortalLayout';
import { useAccountContextStore } from './features/account-context/account-context-store';
import { useFilterStore } from './features/filters/filter-store';
import { CampaignDetailRoute, CampaignsRoute } from './routes/campaigns';
import { BrandDetailRoute, BrandsRoute } from './routes/brands';
import { DashboardRoute } from './routes/dashboard';
import { LoginRoute } from './routes/login';
import { MessagesRoute } from './routes/messages';
import { NavigatorRoute } from './routes/navigator';
import { OptOutsRoute } from './routes/opt-outs';
import { KeywordsRoute } from './routes/keywords';
import { ReportsIndexRoute, WebhooksReportRoute } from './routes/reports';
import { SettingsRoute } from './routes/settings';
import { SupportNewTicketRoute } from './routes/support';
import { SupportTicketsListRoute } from './routes/support-list';
import { TemplatesRoute } from './routes/templates';
import type { TimeDisplayMode } from './utils/datetime';

export function App() {
  const accountContext = useAccountContextStore();
  const filters = useFilterStore();
  const [timeDisplayMode, setTimeDisplayMode] = useState<TimeDisplayMode>('local');
  const accountId = accountContext.selectedAccountId;
  const selectedAccount = accountContext.accounts.find((account) => account.id === accountId) ?? null;

  if (!accountContext.loading && !accountContext.session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginRoute session={null} onSessionRefresh={accountContext.reload} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (!accountContext.loading && accountContext.session && !accountId) {
    return (
      <Routes>
        <Route
          path="/login"
          element={<LoginRoute session={accountContext.session} onSessionRefresh={accountContext.reload} />}
        />
        <Route path="/account-select" element={<AccountSelectionRoute accountContext={accountContext} />} />
        <Route path="*" element={<Navigate to="/account-select" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={<LoginRoute session={accountContext.session} onSessionRefresh={accountContext.reload} />}
      />
      <Route
        path="/"
        element={
          <PortalLayout
            session={accountContext.session}
            timeDisplayMode={timeDisplayMode}
            onChangeTimeDisplayMode={setTimeDisplayMode}
          />
        }
      >
        <Route index element={<DashboardRoute accountContext={accountContext} filters={filters} />} />
        <Route path="brands" element={<BrandsRoute accountId={accountId} />} />
        <Route path="brands/:brandId" element={<BrandDetailRoute accountId={accountId} />} />
        <Route path="campaigns" element={<CampaignsRoute accountId={accountId} />} />
        <Route path="campaigns/:campaignId" element={<CampaignDetailRoute accountId={accountId} />} />
        <Route
          path="messages"
          element={
            <MessagesRoute
              accountId={accountId}
              timeDisplayMode={timeDisplayMode}
              messagingCapabilities={selectedAccount?.messagingCapabilities ?? null}
            />
          }
        />
        <Route path="navigator" element={<NavigatorRoute accountId={accountId} />} />
        <Route path="templates" element={<TemplatesRoute accountId={accountId} timeDisplayMode={timeDisplayMode} />} />
        <Route path="opt-outs" element={<OptOutsRoute timeDisplayMode={timeDisplayMode} />} />
        <Route path="keywords" element={<KeywordsRoute timeDisplayMode={timeDisplayMode} />} />
        <Route path="billing" element={<BillingRoute accountContext={accountContext} filters={filters} />} />
        <Route path="reports" element={<ReportsIndexRoute timeDisplayMode={timeDisplayMode} />} />
        <Route path="reports/webhooks" element={<WebhooksReportRoute timeDisplayMode={timeDisplayMode} />} />
        <Route path="settings" element={<SettingsRoute accountId={accountId} />} />
        <Route path="support" element={<SupportLayout />}>
          <Route index element={<SupportTicketsListRoute accountId={accountId} timeDisplayMode={timeDisplayMode} />} />
          <Route
            path="new"
            element={
              <SupportNewTicketRoute
                accountContext={accountContext}
                filters={filters}
                timeDisplayMode={timeDisplayMode}
              />
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function SupportLayout() {
  return <Outlet />;
}

function AccountSelectionRoute({ accountContext }: { accountContext: ReturnType<typeof useAccountContextStore> }) {
  if (accountContext.selectedAccountId) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-lg-8">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Choose account</h3>
          </div>
          <div className="card-body d-flex flex-column gap-2">
            <div className="text-secondary small">
              Select the environment you want to enter. You can switch accounts later.
            </div>
            {accountContext.switchError ? <div className="alert alert-danger mb-0">{accountContext.switchError}</div> : null}
            {accountContext.accounts.map((account) => (
              <button
                key={`${account.organizationId ?? 'org'}:${account.id}`}
                type="button"
                className="btn btn-outline-primary text-start"
                onClick={() => accountContext.setSelectedAccountId(account.id)}
              >
                <span className="fw-semibold">{account.name}</span>
                <span className="text-secondary ms-2">({account.accountType})</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
